import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { BodyLarge, BodyNormal, FluidContainer, H3, H5 } from '@atlassian/mpac-primitives';
import { css } from '@compiled/react';
import React from 'react';

import type { BundleConfig } from '../config/bundleConfig';

const benefitsSectionStyles = css({
  position: 'relative',
  paddingTop: '6rem',
  paddingBottom: token('space.800'),
});

const benefitsBackgroundStyles = css({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: -1,
  clipPath: 'polygon(0 32px, 100% 0, 100% 100%, 0 100%)',
  background: '#F8F8F8',
});

const benefitsHeaderStyles = css({
  textAlign: 'center',
  marginBottom: token('space.400'),
  maxWidth: '48rem',
});

const benefitsGridStyles = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  columnGap: '5rem',
  rowGap: token('space.600'),
  marginTop: token('space.600'),
  justifyItems: 'center',
  maxWidth: '1200px',
  marginLeft: 'auto',
  marginRight: 'auto',
  [media.above.lg]: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '5rem',
  },
  [media.below.lg]: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: token('space.500'),
  },
  [media.below.md]: {
    gridTemplateColumns: '1fr',
    maxWidth: '25rem',
  },
});

const benefitCardStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

const benefitIconStyles = css({
  width: '4rem',
  height: '4rem',
  marginBottom: token('space.200'),
});

const benefitTitleStyles = css({
  marginTop: 0,
  fontWeight: 700,
  marginBottom: token('space.200'),
});

const benefitDescriptionStyles = css({
  margin: 0,
});

const h3Styles = css({
  fontWeight: '700',
});

const benefitsHeaderContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

interface BenefitCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface BenefitsSectionProps {
  bundleConfig: BundleConfig;
}

const BenefitCard = ({ title, description, icon }: BenefitCardProps) => {
  return (
    <div css={benefitCardStyles}>
      <div css={benefitIconStyles}>{icon}</div>
      <H5 css={benefitTitleStyles}>{title}</H5>
      <BodyNormal css={benefitDescriptionStyles}>{description}</BodyNormal>
    </div>
  );
};

export const BenefitsSection: React.FC<BenefitsSectionProps> = ({ bundleConfig }) => {
  return (
    <div css={benefitsSectionStyles}>
      <div css={benefitsBackgroundStyles} />
      <FluidContainer>
        <div css={benefitsHeaderContainerStyles}>
          <div css={benefitsHeaderStyles}>
            <H3 css={h3Styles}>{bundleConfig.benefits.title}</H3>
            {bundleConfig.benefits.description && (
              <BodyLarge>{bundleConfig.benefits.description}</BodyLarge>
            )}
          </div>
        </div>

        <div css={benefitsGridStyles}>
          {bundleConfig.benefits.items.map((benefit) => (
            <BenefitCard
              key={benefit.title}
              icon={
                benefit.icon ? (
                  <img src={benefit.icon} alt={benefit.iconAlt} width="64px" height="64px" />
                ) : null
              }
              title={benefit.title}
              description={benefit.description}
            />
          ))}
        </div>
      </FluidContainer>
    </div>
  );
};
